import { FOOD_SCORE, PROGRESSIVE_MIN_MS, PROGRESSIVE_STEP_MS } from "../config";
import { placeFood } from "./food";
import { isInside, wrapPoint } from "./grid";
import { DIRECTIONS, isDirectionName, type Direction, type GameOptions, type GridSize, type Point } from "./types";

/** Tek bir oyun turunun tüm durumu — render ve input'tan bağımsız saf veri. */
export interface PlayState {
  grid: GridSize;
  /** Kafa = index 0. */
  snake: Point[];
  dir: Direction;
  /** Ardışık girişleri sıraya alır; anlık ters dönüşü engellemek için. */
  dirQueue: Direction[];
  food: Point;
  score: number;
  foodEaten: number;
}

/** Bir tick'in sonucu. */
export type TickResult = "playing" | "dead";

export function createPlay(grid: GridSize): PlayState {
  const cx = Math.floor(grid.cols / 2);
  const cy = Math.floor(grid.rows / 2);
  const snake: Point[] = [
    { x: cx, y: cy },
    { x: cx - 1, y: cy },
    { x: cx - 2, y: cy },
  ];
  return {
    grid,
    snake,
    dir: { ...DIRECTIONS.right },
    dirQueue: [],
    food: placeFood(grid, snake),
    score: 0,
    foodEaten: 0,
  };
}

/** Yön girişini kuyruğa alır; ters ve tekrarlı yönleri yok sayar. */
export function enqueueDirection(play: PlayState, name: string): void {
  if (!isDirectionName(name)) return;
  const next = DIRECTIONS[name];
  const last = play.dirQueue.length > 0 ? play.dirQueue[play.dirQueue.length - 1] : play.dir;
  if (next.x === -last.x && next.y === -last.y) return; // ters dönüş
  if (next.x === last.x && next.y === last.y) return; // aynı yön
  if (play.dirQueue.length < 2) play.dirQueue.push(next);
}

/** Şu anki hız moduna göre bir tick'in süresi (ms). */
export function tickIntervalMs(opts: GameOptions, foodEaten: number): number {
  if (!opts.progressive) return opts.baseTickMs;
  return Math.max(PROGRESSIVE_MIN_MS, opts.baseTickMs - foodEaten * PROGRESSIVE_STEP_MS);
}

/**
 * Yılanı bir kare ilerletir. Dönüş değeri `"dead"` ise tur biter;
 * play durumu artık geçerli değildir, yalnızca çizim için kullanılabilir.
 */
export function tickPlay(play: PlayState, opts: GameOptions): TickResult {
  if (play.dirQueue.length > 0) play.dir = play.dirQueue.shift() ?? play.dir;

  const next: Point = {
    x: play.snake[0].x + play.dir.x,
    y: play.snake[0].y + play.dir.y,
  };

  if (opts.wallPass) {
    const wrapped = wrapPoint(next, play.grid);
    next.x = wrapped.x;
    next.y = wrapped.y;
  } else if (!isInside(next, play.grid)) {
    return "dead"; // duvara çarptı
  }

  // Kuyruk aynı adımda hareket edeceği için son hücre hariç kendine çarpma kontrolü;
  // yem yenilecekse kuyruk sabit kalır, o zaman son hücre de kontrol edilir.
  const willEat = next.x === play.food.x && next.y === play.food.y;
  const checkLength = play.snake.length - (willEat ? 0 : 1);
  for (let i = 0; i < checkLength; i++) {
    if (play.snake[i].x === next.x && play.snake[i].y === next.y) return "dead";
  }

  play.snake.unshift(next);
  if (willEat) {
    play.score += FOOD_SCORE;
    play.foodEaten += 1;
    play.food = placeFood(play.grid, play.snake);
  } else {
    play.snake.pop();
  }
  return "playing";
}
